import React from 'react'
import PropTypes from 'prop-types'
import styled, { css, withTheme } from 'styled-components'
import { Fade } from 'react-reveal'

import Container from './Container'
import Title from './Title'
import Button from './Button'

import config from '../config'
import { buildCTA } from '../utils'

import step1Img from '../images/step_01.svg'
import step2Img from '../images/step_02.svg'
import step3Img from '../images/step_03.svg'

import step1GrayImg from '../images/step_01_gray.svg'
import step2GrayImg from '../images/step_02_gray.svg'
import step3GrayImg from '../images/step_03_gray.svg'

import step1LightblueImg from '../images/step_01_lightblue.svg'
import step2LightblueImg from '../images/step_02_lightblue.svg'
import step3LightblueImg from '../images/step_03_lightblue.svg'

const StyledTitle = styled(Title)`
  margin-bottom: 55px;
  max-width: ${props => (props.wideTitle ? '1180px' : '960px')};
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 460px) {
    text-align: left;
  }
`

const Background = styled.div`
  ${props =>
    props.background &&
    css`
      padding-top: 100px;
      padding-bottom: 80px;
      background: #f4f4f3;
    `}
`

const Icon = styled.img`
  margin: 0 auto 30px auto;
  display: block;

  @media (max-width: 460px) {
    display: none;
  }

  svg {
    fill: red;
  }
`

const Main = styled.div`
  position: relative;
  display: flex;

  ${props =>
    props.yAlign &&
    css`
      align-items: center;

      @media (min-width: 620px) {
        min-height: 84px;
      }
    `}
`

const Count = styled.div`
  font-size: 40px;
  font-family: 'Guardian Egyptian Web';
  font-weight: 400;
  font-style: normal;
  font-stretch: normal;
  color: ${config.colors.blue};
  line-height: 1.2;
  margin-right: 6px;
  margin-top: -0.22em;

  @media (max-width: 480px) {
    margin-top: -0.25em;
  }

  ${props =>
    (props.theme.name === 'blue' ||
      props.theme.name === 'platinum' ||
      props.theme.name === 'multi') &&
    css`
      font-family: 'BentonSans';
      font-weight: 900;
      font-size: 35px;
    `}

  ${props =>
    props.theme.name === 'platinum' &&
    css`
      color: #75787b;
    `}

  ${props =>
    props.theme.name === 'payback' &&
    css`
      font-size: 35px;
      width: 60px;
      margin: 0 20px 0 0;
      font-family: 'PAYBACK_Bold';
      font-weight: 700;
      color: ${config.colors.peacockBlue};
    `}
`

const Text = styled.div``

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
`

const ListItem = styled.li`
  flex: 1;
  margin-bottom: 40px;
  min-width: 280px;
`

const Actions = styled.div`
  text-align: center;
  margin-top: 25px;
`

const Item = styled.div`
  max-width: 330px;
  margin: 0 auto;
  line-height: 1.4;

  ${props =>
    props.theme.name === 'payback' &&
    css`
      max-width: 280px;
    `}

  @media (max-width: 460px) {
    margin: 0;
  }
`

const Space = styled.span`
  @media (max-width: 460px) {
    display: block;
    margin-top: 0.5em;
  }
`

const leftPad = count => {
  return `${count}`.padStart(2, '0')
}

const Step = ({ count, icon, children, twoDigits = false, yAlign = false }) => (
  <Item>
    <Icon src={icon} alt="" />
    <Main yAlign={yAlign}>
      <Count>{twoDigits ? leftPad(count) : count}.</Count>
      <Text>{children}</Text>
    </Main>
  </Item>
)

const Steps = ({
  title,
  step1,
  step2,
  wideTitle,
  theme,
  noAction,
  background,
  variant,
  twoDigits,
  yAlign = false,
}) => {
  return (
    <Container wide={background} noPadding>
      <Background background={background}>
        <Container>
          <StyledTitle align="center" wideTitle={wideTitle}>
            {title}
          </StyledTitle>
          <List>
            <ListItem>
              <Fade delay={100} fraction={1} duration={1800}>
                <Step
                  count={1}
                  twoDigits={twoDigits}
                  yAlign={yAlign}
                  icon={
                    theme.name === 'platinum'
                      ? step1GrayImg
                      : theme.name === 'payback'
                      ? step1LightblueImg
                      : step1Img
                  }>
                  {step1}
                </Step>
              </Fade>
            </ListItem>
            <ListItem>
              <Fade delay={100} fraction={1} duration={1800}>
                <Step
                  count={2}
                  twoDigits={twoDigits}
                  yAlign={yAlign}
                  icon={
                    theme.name === 'platinum'
                      ? step2GrayImg
                      : theme.name === 'payback'
                      ? step2LightblueImg
                      : step2Img
                  }>
                  {step2 ?
                    step2
                    :
                    <>
                      Unterschriebenes Formular, Kopie eines Lichtbildausweises sowie Kopie der Bankomatkarte oder eines aktuellen (Online-) Kontoauszuges übermitteln
                    </>
                  }
                </Step>
              </Fade>
            </ListItem>
            <ListItem>
              <Fade delay={100} fraction={1} duration={1800}>
                <Step
                  count={3}
                  twoDigits={twoDigits}
                  yAlign={yAlign}
                  icon={
                    theme.name === 'platinum'
                      ? step3GrayImg
                      : theme.name === 'payback'
                      ? step3LightblueImg
                      : step3Img
                  }>
                  Karte erhalten, freischalten und einsetzen
                </Step>
              </Fade>
            </ListItem>
          </List>
          {!noAction && (
            <Actions>
              <Button href={buildCTA(variant)}>Jetzt beantragen</Button>
            </Actions>
          )}
        </Container>
      </Background>
    </Container>
  )
}

Steps.Space = Space

Steps.propTypes = {
  title: PropTypes.node,
  step1: PropTypes.string,
}

export default withTheme(Steps)
